import { useState, useEffect } from 'react';
import axios from 'axios';
import ManagerLayout from '../components/ManagerLayout';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card } from '../components/ui/card';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { toast } from 'sonner';
import { Plus, Edit, User, Upload, Download, Trash2 } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ManagerEmployees = ({ manager, onLogout }) => {
  const [employees, setEmployees] = useState([]);
  const [filteredEmployees, setFilteredEmployees] = useState([]);
  const [locations, setLocations] = useState([]);
  const [isAddDialogOpen, setIsAddDialogOpen] = useState(false);
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [isBulkUploadOpen, setIsBulkUploadOpen] = useState(false);
  const [uploadResults, setUploadResults] = useState(null);
  const [selectedEmployee, setSelectedEmployee] = useState(null);
  const [filters, setFilters] = useState({
    search: '',
    status: [],
    role: [],
  });
  const [sortBy, setSortBy] = useState('name'); // name, role, status
  const [sortOrder, setSortOrder] = useState('asc'); // asc, desc
  const [formData, setFormData] = useState({
    phone_last4: '',
    full_name: '',
    employee_id: '',
    status: 'active',
    role: 'Associate',
    email: '',
    manager_password: '',
    hourly_rate: '15.50',
    unpaid_breaks: true,
  });

  useEffect(() => {
    fetchEmployees();
    fetchLocations();
  }, []);

  useEffect(() => {
    applyFiltersAndSort();
  }, [employees, filters, sortBy, sortOrder]);

  const applyFiltersAndSort = () => {
    let filtered = [...employees];

    // Apply search filter
    if (filters.search) {
      const searchLower = filters.search.toLowerCase();
      filtered = filtered.filter(
        (emp) =>
          emp.full_name.toLowerCase().includes(searchLower) ||
          emp.employee_id.toLowerCase().includes(searchLower)
      );
    }

    // Apply status filter
    if (filters.status.length > 0) {
      filtered = filtered.filter((emp) => filters.status.includes(emp.status));
    }

    // Apply role filter
    if (filters.role.length > 0) {
      filtered = filtered.filter((emp) => filters.role.includes(emp.role));
    }

    // Apply sorting
    filtered.sort((a, b) => {
      let compareA, compareB;

      switch (sortBy) {
        case 'name':
          compareA = a.full_name.toLowerCase();
          compareB = b.full_name.toLowerCase();
          break;
        case 'role':
          compareA = a.role.toLowerCase();
          compareB = b.role.toLowerCase();
          break;
        case 'status':
          compareA = a.status.toLowerCase();
          compareB = b.status.toLowerCase();
          break;
        default:
          compareA = a.full_name.toLowerCase();
          compareB = b.full_name.toLowerCase();
      }

      if (compareA < compareB) return sortOrder === 'asc' ? -1 : 1;
      if (compareA > compareB) return sortOrder === 'asc' ? 1 : -1;
      return 0;
    });

    setFilteredEmployees(filtered);
  };

  const handleClearFilters = () => {
    setFilters({
      search: '',
      status: [],
      role: [],
    });
    setSortBy('name');
    setSortOrder('asc');
  };

  const fetchEmployees = async () => {
    try {
      const token = sessionStorage.getItem('managerToken');
      const response = await axios.get(`${API}/employees`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setEmployees(response.data);
    } catch (error) {
      toast.error('Error fetching employees');
    }
  };

  const fetchLocations = async () => {
    try {
      const response = await axios.get(`${API}/locations`);
      setLocations(response.data);
    } catch (error) {
      toast.error('Error fetching locations');
    }
  };

  const handleAddEmployee = async () => {
    if (
      !formData.phone_last4 ||
      !formData.full_name ||
      !formData.email
    ) {
      toast.error('Please fill in all required fields (Name, Email, and Phone)');
      return;
    }

    // Validate email format
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(formData.email)) {
      toast.error('Please enter a valid email address');
      return;
    }

    try {
      const token = sessionStorage.getItem('managerToken');
      // Auto-generate employee_id from email if not provided
      const dataToSend = {
        ...formData,
        employee_id: formData.employee_id || formData.email.split('@')[0].toUpperCase(),
      };
      await axios.post(`${API}/employees`, dataToSend, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success('Employee added successfully');
      setIsAddDialogOpen(false);
      setFormData({
        phone_last4: '',
        full_name: '',
        employee_id: '',
        status: 'active',
        role: 'Associate',
        email: '',
        manager_password: '',
        hourly_rate: '15.50',
        unpaid_breaks: true,
      });
      fetchEmployees();
    } catch (error) {
      toast.error(
        error.response?.data?.detail || 'Error adding employee'
      );
    }
  };

  const handleEditEmployee = async () => {
    try {
      const token = sessionStorage.getItem('managerToken');
      const updateData = {
        phone_last4: formData.phone_last4,
        full_name: formData.full_name,
        employee_id: formData.employee_id,
        status: formData.status,
        role: formData.role,
        hourly_rate: parseFloat(formData.hourly_rate) || 15.50,
        unpaid_breaks: formData.unpaid_breaks,
      };
      
      // Only include manager credentials if they are provided
      if (formData.email) {
        updateData.email = formData.email;
      }
      if (formData.manager_password) {
        updateData.manager_password = formData.manager_password;
      }
      
      await axios.put(
        `${API}/employees/${selectedEmployee.id}`,
        updateData,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      toast.success('Employee updated successfully');
      setIsEditDialogOpen(false);
      setSelectedEmployee(null);
      fetchEmployees();
    } catch (error) {
      toast.error(
        error.response?.data?.detail || 'Error updating employee'
      );
    }
  };

  const handleDeleteEmployee = async () => {
    try {
      const token = sessionStorage.getItem('managerToken');
      await axios.delete(`${API}/employees/${selectedEmployee.id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success('Employee deleted successfully');
      setIsDeleteDialogOpen(false);
      setSelectedEmployee(null);
      fetchEmployees();
    } catch (error) {
      toast.error(
        error.response?.data?.detail || 'Error deleting employee'
      );
    }
  };

  const handleBulkUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    if (!file.name.endsWith('.csv')) {
      toast.error('Please upload a CSV file');
      return;
    }

    const formData = new FormData();
    formData.append('file', file);

    try {
      const token = sessionStorage.getItem('managerToken');
      const response = await axios.post(`${API}/employees/bulk-upload`, formData, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'multipart/form-data',
        },
      });

      setUploadResults(response.data);
      toast.success(
        `Upload complete! ${response.data.success.length} employees added, ${response.data.errors.length} errors`
      );
      fetchEmployees();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error uploading CSV');
    }

    // Reset file input
    event.target.value = '';
  };

  const downloadSampleCSV = () => {
    const csv = `full_name,email,phone_last4,role,unpaid_breaks
John Doe,john.doe@company.com,1234,Associate,Yes
Jane Smith,jane.smith@company.com,5678,Manager,Yes
Bob Johnson,bob.johnson@company.com,9012,Associate,No`;

    const blob = new Blob([csv], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'employee_template.csv';
    a.click();
    window.URL.revokeObjectURL(url);
  };

  const openEditDialog = (employee) => {
    setSelectedEmployee(employee);
    setFormData({
      phone_last4: employee.phone_last4,
      full_name: employee.full_name,
      employee_id: employee.employee_id,
      status: employee.status,
      role: employee.role || 'Associate',
      email: employee.email || '',
      manager_password: '', // Don't pre-fill password for security
      hourly_rate: employee.hourly_rate || '15.50',
      unpaid_breaks: employee.unpaid_breaks !== false, // Default to true if not set
    });
    setIsEditDialogOpen(true);
  };

  const openDeleteDialog = (employee) => {
    setSelectedEmployee(employee);
    setIsDeleteDialogOpen(true);
  };

  const getLocationName = (locationId) => {
    if (!locationId) return 'All Locations';
    const location = locations.find((loc) => loc.id === locationId);
    return location ? location.name : 'All Locations';
  };

  return (
    <ManagerLayout manager={manager} onLogout={onLogout}>
      <div className="mb-8 flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold text-gray-800 mb-2">Employees</h1>
          <p className="text-gray-600">Manage your employee roster</p>
        </div>

        <div className="flex gap-3">
          <Button
            onClick={downloadSampleCSV}
            variant="outline"
            data-testid="download-template-button"
          >
            <Download className="w-4 h-4 mr-2" />
            Download CSV Template
          </Button>

          <label htmlFor="bulk-upload" className="cursor-pointer">
            <Button
              variant="outline"
              className="relative"
              data-testid="bulk-upload-button"
              onClick={() => document.getElementById('bulk-upload').click()}
            >
              <Upload className="w-4 h-4 mr-2" />
              Bulk Upload CSV
            </Button>
            <input
              id="bulk-upload"
              type="file"
              accept=".csv"
              className="hidden"
              onChange={handleBulkUpload}
            />
          </label>

          <Dialog open={isAddDialogOpen} onOpenChange={setIsAddDialogOpen}>
            <DialogTrigger asChild>
              <Button
                className="bg-gradient-to-r from-gray-900 to-black hover:from-gray-800 hover:to-gray-900 text-white shadow-lg"
                data-testid="add-employee-button"
              >
                <Plus className="w-4 h-4 mr-2" />
                Add Employee
              </Button>
            </DialogTrigger>
          <DialogContent data-testid="add-employee-dialog">
            <DialogHeader>
              <DialogTitle>Add New Employee</DialogTitle>
            </DialogHeader>
            <div className="space-y-4 mt-4">
              <p className="text-sm text-amber-700 bg-amber-50 p-3 rounded border border-amber-200">
                Must be the same as QuickBooks employee information
              </p>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Full Name <span className="text-red-500">*</span>
                </label>
                <Input
                  placeholder="John Doe"
                  value={formData.full_name}
                  onChange={(e) =>
                    setFormData({ ...formData, full_name: e.target.value })
                  }
                  data-testid="employee-full-name-input"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Email Address <span className="text-red-500">*</span>
                </label>
                <Input
                  type="email"
                  placeholder="employee@example.com"
                  value={formData.email}
                  onChange={(e) =>
                    setFormData({ ...formData, email: e.target.value })
                  }
                  data-testid="employee-email-input"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Phone (Last 4 Digits) <span className="text-red-500">*</span>
                </label>
                <Input
                  maxLength="4"
                  placeholder="1234"
                  value={formData.phone_last4}
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      phone_last4: e.target.value.replace(/\D/g, ''),
                    })
                  }
                  data-testid="employee-phone-input"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Role <span className="text-red-500">*</span>
                </label>
                <Select
                  value={formData.role}
                  onValueChange={(value) =>
                    setFormData({ ...formData, role: value })
                  }
                >
                  <SelectTrigger data-testid="employee-role-select">
                    <SelectValue placeholder="Select role" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Associate">Associate</SelectItem>
                    <SelectItem value="Manager">Manager</SelectItem>
                    {manager?.role === 'Administrator' && (
                      <SelectItem value="Administrator">Administrator</SelectItem>
                    )}
                  </SelectContent>
                </Select>
              </div>
              
              {(formData.role === 'Manager' || formData.role === 'Administrator') && (
                <>
                  <div className="bg-blue-50 p-3 rounded-lg border border-blue-200">
                    <p className="text-sm text-blue-800 font-medium mb-2">
                      Manager Portal Credentials
                    </p>
                    <p className="text-xs text-blue-600">
                      Email above will be used for portal login. 
                      Punch clock will still use the 4-digit phone PIN.
                    </p>
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Manager Password <span className="text-red-500">*</span>
                    </label>
                    <Input
                      type="password"
                      placeholder="Min 8 chars, 1 upper, 1 lower, 1 number"
                      value={formData.manager_password}
                      onChange={(e) =>
                        setFormData({ ...formData, manager_password: e.target.value })
                      }
                      data-testid="manager-password-input"
                    />
                    <p className="text-xs text-gray-500 mt-1">
                      Password must have at least 8 characters with 1 uppercase, 1 lowercase, and 1 number
                    </p>
                  </div>
                </>
              )}
              
              <div className="flex items-center justify-between py-3 px-4 bg-gray-50 rounded-lg">
                <div>
                  <label className="text-sm font-medium text-gray-700">
                    Unpaid 30 min break
                  </label>
                  <p className="text-xs text-gray-500">
                    Automatically deduct 30 min break for shifts 4+ hours
                  </p>
                </div>
                <button
                  type="button"
                  onClick={() => setFormData({ ...formData, unpaid_breaks: !formData.unpaid_breaks })}
                  className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                    formData.unpaid_breaks ? 'bg-green-600' : 'bg-gray-300'
                  }`}
                  data-testid="unpaid-breaks-toggle"
                >
                  <span
                    className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                      formData.unpaid_breaks ? 'translate-x-6' : 'translate-x-1'
                    }`}
                  />
                </button>
              </div>
              
              <Button
                onClick={handleAddEmployee}
                className="w-full"
                data-testid="submit-add-employee"
              >
                Add Employee
              </Button>
            </div>
          </DialogContent>
          </Dialog>
        </div>
      </div>

      {/* Filters Card */}
      <Card className="p-6 mb-6">
        <h2 className="text-lg font-semibold text-gray-800 mb-4">
          Filter & Sort Employees
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Search
            </label>
            <Input
              placeholder="Search by name or employee ID"
              value={filters.search}
              onChange={(e) =>
                setFilters({ ...filters, search: e.target.value })
              }
              data-testid="employee-search-input"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Status (Multi-select)
            </label>
            <div className="border border-gray-300 rounded-md p-3 space-y-2">
              {['active', 'inactive'].map((status) => (
                <label key={status} className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={filters.status.includes(status)}
                    onChange={(e) => {
                      if (e.target.checked) {
                        setFilters({ 
                          ...filters, 
                          status: [...filters.status, status] 
                        });
                      } else {
                        setFilters({ 
                          ...filters, 
                          status: filters.status.filter(s => s !== status) 
                        });
                      }
                    }}
                    className="w-4 h-4"
                  />
                  <span className="text-sm capitalize">{status}</span>
                </label>
              ))}
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Role (Multi-select)
            </label>
            <div className="border border-gray-300 rounded-md p-3 space-y-2">
              {['Administrator', 'Manager', 'Associate'].map((role) => (
                <label key={role} className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={filters.role.includes(role)}
                    onChange={(e) => {
                      if (e.target.checked) {
                        setFilters({ 
                          ...filters, 
                          role: [...filters.role, role] 
                        });
                      } else {
                        setFilters({ 
                          ...filters, 
                          role: filters.role.filter(r => r !== role) 
                        });
                      }
                    }}
                    className="w-4 h-4"
                  />
                  <span className="text-sm">{role}</span>
                </label>
              ))}
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Sort By
            </label>
            <div className="grid grid-cols-2 gap-2">
              <div>
                <Select value={sortBy} onValueChange={setSortBy}>
                  <SelectTrigger data-testid="sort-by-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="name">Name</SelectItem>
                    <SelectItem value="role">Role</SelectItem>
                    <SelectItem value="status">Status</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Select value={sortOrder} onValueChange={setSortOrder}>
                  <SelectTrigger data-testid="sort-order-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="asc">A-Z</SelectItem>
                    <SelectItem value="desc">Z-A</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          </div>
        </div>

        <div className="mt-4 flex justify-between items-center">
          <p className="text-sm text-gray-600">
            Showing <strong>{filteredEmployees.length}</strong> of <strong>{employees.length}</strong> employees
          </p>
          <Button
            onClick={handleClearFilters}
            variant="outline"
            size="sm"
            data-testid="clear-filters-button"
          >
            Clear Filters
          </Button>
        </div>
      </Card>

      <Card className="p-6">
        <div className="overflow-x-auto">
          <table className="w-full" data-testid="employees-table">
            <thead>
              <tr className="border-b">
                <th className="text-left py-3 px-4 text-sm font-semibold text-gray-700">
                  Name
                </th>
                <th className="text-left py-3 px-4 text-sm font-semibold text-gray-700">
                  Username (Email)
                </th>
                <th className="text-left py-3 px-4 text-sm font-semibold text-gray-700">
                  Employee ID
                </th>
                <th className="text-left py-3 px-4 text-sm font-semibold text-gray-700">
                  Phone (Last 4)
                </th>
                <th className="text-left py-3 px-4 text-sm font-semibold text-gray-700">
                  Role
                </th>
                <th className="text-left py-3 px-4 text-sm font-semibold text-gray-700">
                  Status
                </th>
                <th className="text-left py-3 px-4 text-sm font-semibold text-gray-700">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody>
              {filteredEmployees.map((employee) => (
                <tr
                  key={employee.id}
                  className="border-b hover:bg-gray-50"
                  data-testid={`employee-row-${employee.id}`}
                >
                  <td className="py-3 px-4">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 bg-gradient-to-br from-gray-900 to-black rounded-full flex items-center justify-center">
                        <User className="w-5 h-5 text-white" />
                      </div>
                      <span className="font-medium text-gray-800">
                        {employee.full_name}
                      </span>
                    </div>
                  </td>
                  <td className="py-3 px-4 text-gray-600 text-sm">
                    {employee.email || <span className="text-gray-400 italic">Not set</span>}
                  </td>
                  <td className="py-3 px-4 text-gray-600">
                    {employee.employee_id}
                  </td>
                  <td className="py-3 px-4 text-gray-600">
                    {employee.phone_last4}
                  </td>
                  <td className="py-3 px-4">
                    <span
                      className={`px-3 py-1 rounded-full text-xs font-semibold ${
                        employee.role === 'Administrator'
                          ? 'bg-purple-100 text-purple-800'
                          : employee.role === 'Manager'
                          ? 'bg-blue-100 text-blue-800'
                          : 'bg-gray-100 text-gray-800'
                      }`}
                    >
                      {employee.role || 'Associate'}
                    </span>
                  </td>
                  <td className="py-3 px-4">
                    <span
                      className={`px-3 py-1 rounded-full text-xs font-semibold ${
                        employee.status === 'active'
                          ? 'bg-green-100 text-green-800'
                          : 'bg-red-100 text-red-800'
                      }`}
                    >
                      {employee.status}
                    </span>
                  </td>
                  <td className="py-3 px-4">
                    <div className="flex gap-2">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => openEditDialog(employee)}
                        data-testid={`edit-employee-${employee.id}`}
                      >
                        <Edit className="w-4 h-4" />
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => openDeleteDialog(employee)}
                        data-testid={`delete-employee-${employee.id}`}
                        className="text-red-600 hover:text-red-700 hover:bg-red-50"
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>

      {/* Edit Dialog */}
      <Dialog open={isEditDialogOpen} onOpenChange={setIsEditDialogOpen}>
        <DialogContent data-testid="edit-employee-dialog">
          <DialogHeader>
            <DialogTitle>Edit Employee</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Full Name
              </label>
              <Input
                value={formData.full_name}
                onChange={(e) =>
                  setFormData({ ...formData, full_name: e.target.value })
                }
                data-testid="edit-employee-full-name-input"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Employee ID
              </label>
              <Input
                value={formData.employee_id}
                onChange={(e) =>
                  setFormData({ ...formData, employee_id: e.target.value })
                }
                data-testid="edit-employee-id-input"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Phone (Last 4 Digits)
              </label>
              <Input
                maxLength="4"
                value={formData.phone_last4}
                onChange={(e) =>
                  setFormData({
                    ...formData,
                    phone_last4: e.target.value.replace(/\D/g, ''),
                  })
                }
                data-testid="edit-employee-phone-input"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Hourly Rate (for payroll calculations)
              </label>
              <Input
                type="number"
                step="0.01"
                min="0"
                placeholder="15.50"
                value={formData.hourly_rate}
                onChange={(e) =>
                  setFormData({
                    ...formData,
                    hourly_rate: e.target.value,
                  })
                }
                data-testid="edit-employee-hourly-rate-input"
              />
              <p className="text-xs text-gray-500 mt-1">Ontario minimum wage: $15.50/hr</p>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Role
              </label>
              <Select
                value={formData.role}
                onValueChange={(value) =>
                  setFormData({ ...formData, role: value })
                }
              >
                <SelectTrigger data-testid="edit-employee-role-select">
                  <SelectValue placeholder="Select role" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="Associate">Associate</SelectItem>
                  <SelectItem value="Manager">Manager</SelectItem>
                  {manager?.role === 'Administrator' && (
                    <SelectItem value="Administrator">Administrator</SelectItem>
                  )}
                </SelectContent>
              </Select>
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Email Address (Username) <span className="text-red-500">*</span>
              </label>
              <Input
                type="email"
                placeholder="employee@example.com"
                value={formData.email}
                onChange={(e) =>
                  setFormData({ ...formData, email: e.target.value })
                }
                data-testid="edit-employee-email-input"
              />
              <p className="text-xs text-gray-500 mt-1">
                Required for all employees. Used as unique identifier in reports.
              </p>
            </div>
            
            {(formData.role === 'Manager' || formData.role === 'Administrator') && (
              <>
                <div className="bg-blue-50 p-3 rounded-lg border border-blue-200">
                  <p className="text-sm text-blue-800 font-medium mb-2">
                    Manager Portal Credentials
                  </p>
                  <p className="text-xs text-blue-600">
                    Email above will be used for portal login. Leave password empty to keep existing.
                  </p>
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Reset Password (optional)
                  </label>
                  <Input
                    type="password"
                    placeholder="Enter new password to reset, or leave empty"
                    value={formData.manager_password}
                    onChange={(e) =>
                      setFormData({ ...formData, manager_password: e.target.value })
                    }
                    data-testid="edit-manager-password-input"
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    Password must have at least 8 characters with 1 uppercase, 1 lowercase, and 1 number
                  </p>
                </div>
              </>
            )}
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Status
              </label>
              <Select
                value={formData.status}
                onValueChange={(value) =>
                  setFormData({ ...formData, status: value })
                }
              >
                <SelectTrigger data-testid="edit-employee-status-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="active">Active</SelectItem>
                  <SelectItem value="inactive">Inactive</SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            <div className="flex items-center justify-between py-3 px-4 bg-gray-50 rounded-lg">
              <div>
                <label className="text-sm font-medium text-gray-700">
                  Unpaid 30 min break
                </label>
                <p className="text-xs text-gray-500">
                  Automatically deduct 30 min break for shifts 4+ hours
                </p>
              </div>
              <button
                type="button"
                onClick={() => setFormData({ ...formData, unpaid_breaks: !formData.unpaid_breaks })}
                className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                  formData.unpaid_breaks ? 'bg-green-600' : 'bg-gray-300'
                }`}
                data-testid="edit-unpaid-breaks-toggle"
              >
                <span
                  className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                    formData.unpaid_breaks ? 'translate-x-6' : 'translate-x-1'
                  }`}
                />
              </button>
            </div>
            
            <Button
              onClick={handleEditEmployee}
              className="w-full"
              data-testid="submit-edit-employee"
            >
              Update Employee
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Bulk Upload Results Dialog */}
      <Dialog open={uploadResults !== null} onOpenChange={() => setUploadResults(null)}>
        <DialogContent data-testid="upload-results-dialog">
          <DialogHeader>
            <DialogTitle>Bulk Upload Results</DialogTitle>
          </DialogHeader>
          {uploadResults && (
            <div className="space-y-4 mt-4 max-h-96 overflow-y-auto">
              <div className="grid grid-cols-3 gap-4 text-center">
                <div className="p-3 bg-blue-50 rounded">
                  <p className="text-2xl font-bold text-blue-600">{uploadResults.total}</p>
                  <p className="text-sm text-gray-600">Total Rows</p>
                </div>
                <div className="p-3 bg-green-50 rounded">
                  <p className="text-2xl font-bold text-green-600">{uploadResults.success.length}</p>
                  <p className="text-sm text-gray-600">Successful</p>
                </div>
                <div className="p-3 bg-red-50 rounded">
                  <p className="text-2xl font-bold text-red-600">{uploadResults.errors.length}</p>
                  <p className="text-sm text-gray-600">Errors</p>
                </div>
              </div>

              {uploadResults.success.length > 0 && (
                <div>
                  <h4 className="font-semibold text-green-800 mb-2">Successfully Added:</h4>
                  <div className="space-y-1 text-sm">
                    {uploadResults.success.map((item) => (
                      <div key={item.row} className="text-gray-700">
                        ✓ Row {item.row}: {item.name} ({item.employee_id})
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {uploadResults.errors.length > 0 && (
                <div>
                  <h4 className="font-semibold text-red-800 mb-2">Errors:</h4>
                  <div className="space-y-1 text-sm">
                    {uploadResults.errors.map((item, idx) => (
                      <div key={idx} className="text-gray-700">
                        ✗ Row {item.row}: {item.error}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              <Button
                onClick={() => setUploadResults(null)}
                className="w-full"
                data-testid="close-results-button"
              >
                Close
              </Button>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={isDeleteDialogOpen} onOpenChange={setIsDeleteDialogOpen}>
        <DialogContent data-testid="delete-employee-dialog">
          <DialogHeader>
            <DialogTitle>Delete Employee</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-4">
            <p className="text-gray-700">
              Are you sure you want to delete{' '}
              <span className="font-semibold">
                {selectedEmployee?.full_name}
              </span>
              ? This action cannot be undone.
            </p>
            <p className="text-sm text-red-600 bg-red-50 p-3 rounded">
              Warning: All timesheet entries for this employee will remain in the system but will no longer be associated with an active employee.
            </p>
            <div className="flex gap-3">
              <Button
                variant="outline"
                onClick={() => setIsDeleteDialogOpen(false)}
                className="flex-1"
                data-testid="cancel-delete-button"
              >
                Cancel
              </Button>
              <Button
                onClick={handleDeleteEmployee}
                className="flex-1 bg-red-600 hover:bg-red-700 text-white"
                data-testid="confirm-delete-button"
              >
                Delete Employee
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </ManagerLayout>
  );
};

export default ManagerEmployees;